import type { GeneratedMockup } from '@/composables/mockupExport';

export function postConfigurationSavedToParent(payload: {
    recordId?: string;
    customerId?: string;
    name: string;
    productName?: string;
    qtySelected?: string | number;
    screenshots: { label: string; dataUrl: string }[];
    /**
     * VDP (publipostage) : nombre de lignes de variation de texte de la création
     * (0 si pas de VDP). Remonté au storefront e-commerce pour la facturation du
     * surcoût VDP au client final (propriété `textvdp` de la ligne de panier).
     */
    textvdp?: number;
    /**
     * Encres/fils choisis (sérigraphie / broderie) : tableau { name, pantone, hex }.
     * Vide en numérique/laser. Remonté au storefront (facturation par couleur) et
     * affiché dans le BAT sous l'aplat 2D.
     */
    selectedInks?: Array<{ name: string; pantone: string | null; hex: string }>;
}) {
    if (typeof window === 'undefined' || window.parent === window) {
        return;
    }
    const message: Record<string, unknown> = {
        eventType: 'configurationSaved',
        Name: payload.name,
        // Toujours présent (0 si pas de VDP) pour que le storefront puisse tarifer.
        textvdp: Math.max(0, Math.floor(payload.textvdp ?? 0)),
        // Toujours présent (tableau vide si numérique/laser).
        selectedInks: Array.isArray(payload.selectedInks) ? payload.selectedInks : [],
        Screenshots: payload.screenshots.map(({ label, dataUrl }) => ({ label, dataUrl }))
    };
    if (payload.recordId) {
        message.RecordID = payload.recordId;
    }
    if (payload.customerId) {
        message.CustomerID = payload.customerId;
    }
    if (payload.productName) {
        message.productName = payload.productName;
    }
    if (payload.qtySelected !== undefined && payload.qtySelected !== null && payload.qtySelected !== '') {
        message.qtyselected = payload.qtySelected;
    }
    window.parent.postMessage(JSON.stringify(message), '*');
}

/**
 * MOCKUPS générés à la validation, poussés au storefront.
 *
 * POURQUOI UN MESSAGE À PART, alors qu'`addToCart` porte déjà `record.mockups` ?
 * Parce que les deux ne servent pas au même moment : `addToCart` crée la ligne
 * de panier et doit rester le message « tout ou rien » de la commande, tandis
 * que les mockups sont une image de communication (aperçu panier, e-mail de
 * confirmation, galerie) qu'une boutique peut vouloir traiter seule, sans
 * déballer le `record`. Une intégration qui ignore ce message ne perd rien.
 *
 * ÉMIS SEULEMENT S'IL Y EN A. Trois verrous en amont ont déjà pu tout couper :
 * le produit (`mockupEnabled` au back-office), la session (storefront ou
 * réglage du site) et le rendu lui-même (une création sans zone remplie ne
 * produit aucune vue). Plutôt que d'envoyer un message vide que l'intégrateur
 * devrait apprendre à ignorer, on **n'émet pas** — c'est le sens de « si elles
 * existent ». Le champ `record.mockups` d'`addToCart`, lui, reste toujours
 * présent (tableau vide) pour qui préfère un champ inconditionnel.
 *
 * ORDRE : ce message part AVANT `addToCart`, les mockups étant rendus avant que
 * la réponse de commande ne soit relayée. `orderId` permet de recoller les deux.
 */
export function postMockupsGeneratedToParent(payload: {
    orderId: string | null;
    productId: string | null;
    variantRef?: string | null;
    mockups: GeneratedMockup[];
}) {
    if (typeof window === 'undefined' || window.parent === window) {
        return;
    }

    const mockups = Array.isArray(payload.mockups) ? payload.mockups : [];
    if (!mockups.length) {
        return;
    }

    const message: Record<string, unknown> = {
        eventType: 'mockupsGenerated',
        orderId: payload.orderId ?? null,
        productId: payload.productId ?? null,
        mockups
    };
    // Absent quand le contexte e-commerce est périmé : mieux vaut pas de variante
    // du tout qu'une variante qui désignerait l'ancien produit.
    if (payload.variantRef) {
        message.variantRef = payload.variantRef;
    }

    window.parent.postMessage(JSON.stringify(message), '*');
}

export function usePostMessage() {
    function askEcommerceProduct(handle: string) {
        const message = {
            eventType: 'askEcommerceProduct',
            handle
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    function askEcommerceCustomerId() {
        const message = {
            eventType: 'askCustomerId'
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    /**
     * Demande à l'HÔTE (CMS) de connecter le client. L'auth (formulaire, SSO Google,
     * redirection) appartient au storefront — le configurateur ne gère JAMAIS le login
     * (iframe tierce : cookies/SSO bloqués). L'hôte, une fois le client connecté,
     * ré-émet `sendCustomerId` → le configurateur reprend l'action en attente
     * (sauvegarde). Contrat portable Medusa/Shopify/PrestaShop.
     * Cf. docs/integration-comptes-sauvegardes-cms.md.
     */
    function requireLogin(reason: 'save' | string = 'save') {
        if (typeof window === 'undefined' || window.parent === window) {
            return;
        }
        window.parent.postMessage(JSON.stringify({ eventType: 'requireLogin', reason }), '*');
    }

    function askRedirect(url: string) {
        const message = {
            eventType: 'redirect',
            url: url
        };
        window.parent.postMessage(JSON.stringify(message), '*');
    }

    function askVariantsData(variantRefs: string[], productHandle?: string) {
        const message = {
            eventType: 'askVariantsData',
            variantRefs,
            productHandle
        };

        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    function askParentUrl() {
        const message = {
            eventType: 'askParentUrl'
        };
        const jsonMessage = JSON.stringify(message);
        window.parent.postMessage(jsonMessage, '*');
    }

    /**
     * Demande à l'hôte les PRIX MINI d'une liste de produits catalogue, en UN seul
     * message (le FO ne pouvant pas pricer sans ouvrir chaque produit ni sans le
     * mapping variante→méthode qui vit côté hôte). L'hôte répond `sendCatalogPrices`.
     * Contrat : cf. docs/integration-comptes-sauvegardes-cms.md (§ prix tunnel).
     *   host → FO : { eventType:'sendCatalogPrices',
     *     prices: { [productId]: { from:{price,qty}, byMethod:{ [printProfileId]:{price,qty} } } | null } }
     * null/absent = pas de prix → produit masqué. byMethod absent = méthode masquée.
     */
    function askCatalogPrices(productIds: string[]) {
        if (typeof window === 'undefined' || window.parent === window || !productIds.length) {
            return;
        }
        window.parent.postMessage(JSON.stringify({ eventType: 'askCatalogPrices', productIds }), '*');
    }

    /**
     * Demande à l'hôte ses SUPPLÉMENTS PAR COMBINAISON DE ZONES d'impression. L'hôte
     * répond `sendZonePrices` (il peut aussi l'envoyer d'office au chargement).
     *   host → FO : { eventType:'sendZonePrices',
     *     zonePrices: [{ id, name, price:"2,52 €", zoneIds:[uuid…], zones:[{id,label}] }] }
     * Cf. stores/zonePricing et docs/integration-postmessage.md.
     */
    function askZonePrices() {
        if (typeof window === 'undefined' || window.parent === window) {
            return;
        }
        window.parent.postMessage(JSON.stringify({ eventType: 'askZonePrices' }), '*');
    }

    return {
        askEcommerceProduct,
        askEcommerceCustomerId,
        askRedirect,
        askVariantsData,
        askParentUrl,
        askCatalogPrices,
        askZonePrices,
        requireLogin
    };
}
